/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import {
  Box,
  Switch,
  FormControlLabel,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

export interface ChartDataset {
  label: string;
  data: number[];
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
  type?: string;
  borderDash?: number[];
  pointRadius?: number;
  fill?: boolean;
  tension?: number;
}

export interface GenericBarChartProps {
  labels: string[];
  datasets: ChartDataset[];
  title?: string;
  yAxisTitle?: string;
  xAxisTitle?: string;
  maxValue?: number;
  height?: number;
  shouldUseHorizontalLabels?: boolean;
  colors?: string[];
  options?: any;
  canShowAsGrid?: boolean;
  /** Si es true, las celdas numéricas de la grilla se alinean a la derecha (incluyendo los encabezados de esas columnas). Por defecto true. */
  rightAlignNumbers?: boolean;
}

const defaultColors = [
  'rgba(75, 192, 192, 0.8)',
  'rgba(162, 198, 230, 0.8)',
  'rgba(255, 159, 64, 0.8)',
  'rgba(153, 102, 255, 0.8)',
  'rgba(255, 99, 132, 0.8)',
  'rgba(54, 162, 235, 0.8)',
  'rgba(255, 205, 86, 0.8)',
  'rgba(201, 203, 207, 0.8)'
];

const defaultBorderColors = [
  'rgba(75, 192, 192, 1)',
  'rgba(150, 190, 235, 1)',
  'rgba(255, 159, 64, 1)',
  'rgba(153, 102, 255, 1)',
  'rgba(255, 99, 132, 1)',
  'rgba(54, 162, 235, 1)',
  'rgba(255, 205, 86, 1)',
  'rgba(201, 203, 207, 1)'
];

export const GenericBarChart: React.FC<GenericBarChartProps> = ({
  labels,
  datasets,
  title = 'Gráfico de Barras',
  yAxisTitle = 'Valores',
  xAxisTitle = 'Períodos',
  maxValue,
  height = 400,
  shouldUseHorizontalLabels,
  colors = defaultColors,
  options: optionsParams,
  canShowAsGrid = false,
  rightAlignNumbers = true,
}) => {
  const [showGrid, setShowGrid] = React.useState(false);

  const { chartData, calculatedMaxValue, shouldUseHorizontal } = React.useMemo(() => {
    const itemCount = labels.length;
    const shouldUseHorizontal = shouldUseHorizontalLabels ?? itemCount <= 12;

    const enhancedDatasets = datasets.map((dataset, index) => ({
      ...dataset,
      backgroundColor: dataset.backgroundColor || colors[index % colors.length],
      borderColor: dataset.borderColor || defaultBorderColors[index % defaultBorderColors.length],
      borderWidth: dataset.borderWidth ?? 2,
    }));

    let calculatedMax = maxValue;
    if (!calculatedMax) {
      let max = 0;
      datasets.forEach(dataset => {
        dataset.data.forEach(value => {
          max = Math.max(max, value);
        });
      });
      calculatedMax = max === 0 ? 100 : Math.floor(max * 1.1);
    }

    return {
      chartData: {
        labels,
        datasets: enhancedDatasets,
      },
      calculatedMaxValue: calculatedMax,
      shouldUseHorizontal
    };
  }, [labels, datasets, maxValue, shouldUseHorizontalLabels, colors]);

  // Formateador numérico para tooltips y eje Y
  const formatNumber = (value: number): string => {
    if (value === undefined || value === null) return '';
    return value.toLocaleString('es-PE');
  };

  const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          boxWidth: 12,
          font: {
            size: 11
          }
        }
      },
      title: {
        display: !!title,
        text: title,
        font: {
          size: 14
        }
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          title: function (context: any) {
            return context[0].label;
          },
          label: function (context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null && context.parsed.y !== undefined) {
              label += formatNumber(context.parsed.y);
            }
            return label;
          }
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#333',
        bodyColor: '#666',
        borderColor: 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
        padding: 10,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: calculatedMaxValue.toFixed(1),
        ticks: {
          font: {
            size: 10
          },
          callback: function (value: any) {
            // Formatear todos los números con el mismo separador decimal que el tooltip
            return formatNumber(value);
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        title: {
          display: true,
          text: yAxisTitle,
          font: {
            size: 12
          }
        }
      },
      x: {
        ticks: {
          font: {
            size: 10
          },
          maxRotation: shouldUseHorizontal ? 0 : 45,
          minRotation: shouldUseHorizontal ? 0 : 45,
          padding: shouldUseHorizontal ? 10 : 5,
          callback: function (this: any, value: any, index: number, values: any[]) {
            const label = this.getLabelForValue(value);
            if (shouldUseHorizontal && label && label.length > 15) {
              return label.substring(0, 15) + '...';
            }
            return label;
          }
        },
        grid: {
          display: false,
        },
        barPercentage: shouldUseHorizontal ? 0.8 : 0.6,
        categoryPercentage: shouldUseHorizontal ? 0.9 : 0.7,
        title: {
          display: false,
          text: xAxisTitle,
          font: {
            size: 12
          }
        }
      },
    },
    animation: {
      duration: 1000,
      easing: 'easeOutQuart' as const
    },
    ...optionsParams,
  };

  // Si se habilita la vista de grilla, desactivamos el título del gráfico
  const finalOptions = React.useMemo(() => {
    if (canShowAsGrid) {
      return {
        ...baseOptions,
        plugins: {
          ...baseOptions.plugins,
          title: {
            ...baseOptions.plugins.title,
            display: false,
          },
        },
      };
    }
    return baseOptions;
  }, [baseOptions, canShowAsGrid]);

  // Generar y descargar Excel con los datos de la grilla
  const handleDownloadExcel = () => {
    // Construir matriz de datos: primera fila encabezados
    const headers = [
      xAxisTitle || 'Etiqueta',
      ...datasets.map(ds => ds.label)
    ];
    const rows: any[][] = [headers];

    // Por cada fila de datos
    labels.forEach((label, rowIdx) => {
      const rowData: any[] = [label];
      datasets.forEach(dataset => {
        const rawValue = dataset.data[rowIdx];
        // Si el valor es número se coloca directamente (Excel lo tratará como número)
        // Si no, se coloca string vacío o el valor convertido
        if (typeof rawValue === 'number' && !isNaN(rawValue)) {
          rowData.push(rawValue);
        } else {
          rowData.push(rawValue !== undefined && rawValue !== null ? String(rawValue) : '');
        }
      });
      rows.push(rowData);
    });

    // Crear hoja de trabajo
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, title || 'Datos');
    // Generar archivo y descargar
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, `${title || 'datos'}.xlsx`);
  };

  // Si no se permite la vista de grilla, mantener el comportamiento original
  if (!canShowAsGrid) {
    return (
      <Box sx={{ height }}>
        <Bar data={chartData as any} options={finalOptions} />
      </Box>
    );
  }

  // Cuando canShowAsGrid = true, mostramos el switch y la vista alternativa
  return (
    <Box sx={{ height, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {showGrid && (
            <Tooltip title="Descargar como Excel">
              <IconButton onClick={handleDownloadExcel} size="small">
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={showGrid}
                onChange={(e) => setShowGrid(e.target.checked)}
                color="primary"
              />
            }
            label={showGrid ? "Ver gráfico" : "Ver grilla"}
          />
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        {showGrid ? (
          <TableContainer sx={{ height: '100%', overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{xAxisTitle || 'Etiqueta'}</TableCell>
                  {datasets.map((dataset, idx) => (
                    <TableCell
                      key={idx}
                      align={rightAlignNumbers ? 'right' : 'left'}
                    >
                      {dataset.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {labels.map((label, rowIdx) => (
                  <TableRow key={rowIdx}>
                    <TableCell>{label}</TableCell>
                    {datasets.map((dataset, colIdx) => {
                      const value = dataset.data[rowIdx];
                      const formattedValue =
                        value !== undefined && value !== null
                          ? typeof value === 'number'
                            ? formatNumber(value)
                            : value
                          : '-';
                      return (
                        <TableCell
                          key={colIdx}
                          align={rightAlignNumbers ? 'right' : 'left'}
                        >
                          {formattedValue}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Bar data={chartData as any} options={finalOptions} />
        )}
      </Box>
    </Box>
  );
};